import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Star } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  formatReviewDate,
  statsFromRatings,
  useProductReviews,
  useReviewPhotoUrl,
  useSubmitReview,
  type ProductReview,
} from "@/lib/reviews";
import { cn } from "@/lib/utils";

export function Stars({ value, size = 14 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={1.4}
          className={cn(i <= Math.round(value) ? "fill-accent text-accent" : "text-border")}
        />
      ))}
    </span>
  );
}

/** Résumé compact affiché sous le titre du produit (uniquement s'il y a des avis). */
export function ReviewSummary({
  count,
  average,
  onClick,
}: {
  count: number;
  average: number;
  onClick?: () => void;
}) {
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
    >
      <Stars value={average} />
      <span className="tabular-nums">{average.toFixed(1).replace(".", ",")}</span>
      <span className="underline underline-offset-4">
        ({count} {count > 1 ? "avis" : "avis"})
      </span>
      <span className="sr-only">Voir les avis clients</span>
    </button>
  );
}

const reviewSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(2, "Veuillez entrer votre prénom")
    .max(40, "Le prénom est trop long"),
  rating: z
    .number({ message: "Veuillez donner une note" })
    .min(1, "Veuillez donner une note")
    .max(5),
  comment: z.string().trim().max(1500, "Le commentaire est trop long"),
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

function ReviewPhoto({ path, author }: { path: string; author: string }) {
  const { data: url } = useReviewPhotoUrl(path);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={`Photo partagée par ${author}`}
      loading="lazy"
      decoding="async"
      className="mt-4 h-28 w-28 border border-border object-cover"
    />
  );
}

function ReviewCard({ review }: { review: ProductReview }) {
  return (
    <li className="border border-border bg-card p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Stars value={review.rating} />
          <span className="text-xs text-muted-foreground">
            {formatReviewDate(review.created_at)}
          </span>
        </div>
        {review.verified_purchase ? (
          <span className="label-xs text-accent">Achat vérifié</span>
        ) : null}
      </div>
      {review.comment ? (
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted-foreground">
          {review.comment}
        </p>
      ) : null}
      {review.photo_url ? (
        <ReviewPhoto path={review.photo_url} author={review.author_first_name} />
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">{review.author_first_name}</p>
    </li>
  );
}

function ReviewDialog({
  open,
  onOpenChange,
  productSlug,
  productName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productSlug: string;
  productName: string;
}) {
  const [photo, setPhoto] = useState<File | null>(null);
  const [hovered, setHovered] = useState(0);
  const submit = useSubmitReview(productSlug);

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: { firstName: "", rating: 0, comment: "" },
  });

  const onSubmit = async (values: ReviewFormValues) => {
    try {
      await submit.mutateAsync({
        productSlug,
        firstName: values.firstName,
        rating: values.rating,
        comment: values.comment,
        photo,
      });
      toast.success("Merci ! Votre avis a bien été publié.");
      form.reset({ firstName: "", rating: 0, comment: "" });
      setPhoto(null);
      onOpenChange(false);
    } catch {
      toast.error("Votre avis n'a pas pu être envoyé. Réessayez dans un instant.");
    }
  };

  const rating = form.watch("rating");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-semibold">Donner mon avis</DialogTitle>
          <DialogDescription>{productName}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label-xs">Prénom</FormLabel>
                  <FormControl>
                    <Input placeholder="Salma" maxLength={40} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rating"
              render={() => (
                <FormItem>
                  <FormLabel className="label-xs">Votre note</FormLabel>
                  <FormControl>
                    <div className="flex gap-1" onMouseLeave={() => setHovered(0)}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          aria-label={`${value} étoile${value > 1 ? "s" : ""}`}
                          onMouseEnter={() => setHovered(value)}
                          onClick={() =>
                            form.setValue("rating", value, { shouldValidate: true })
                          }
                          className="p-0.5"
                        >
                          <Star
                            size={26}
                            strokeWidth={1.3}
                            className={cn(
                              "transition-colors",
                              value <= (hovered || rating)
                                ? "fill-accent text-accent"
                                : "text-border",
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="label-xs">Commentaire</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      maxLength={1500}
                      placeholder="Partagez votre expérience avec cette pièce…"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <p className="label-xs">Photo (optionnel)</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                className="block w-full text-xs text-muted-foreground file:mr-3 file:border file:border-border file:bg-secondary file:px-3 file:py-2 file:text-xs file:text-foreground"
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={submit.isPending}
                className="btn-primary label-xs h-12 w-full"
              >
                {submit.isPending ? "Envoi en cours…" : "Publier mon avis"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** Section « Avis clients » affichée sous chaque fiche produit. */
export function ProductReviews({
  productSlug,
  productName,
}: {
  productSlug: string;
  productName: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: reviews = [], isLoading } = useProductReviews(productSlug);
  const stats = statsFromRatings(reviews.map((r) => r.rating));

  return (
    <section id="avis" className="border-t border-border bg-secondary/30 py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-xs text-accent">Avis clients</p>
            <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight sm:text-3xl">
              Ce qu'en pensent nos clientes
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-secondary label-xs h-12 px-6"
          >
            Donner mon avis
          </button>
        </div>

        {isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Chargement des avis…</p>
        ) : stats.count === 0 ? (
          <div className="mt-8 border border-border bg-card p-8 text-center">
            <p className="font-serif text-lg">Soyez le premier à donner votre avis.</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Votre retour aide les autres clientes à choisir leur pièce.
            </p>
          </div>
        ) : (
          <div className="mt-8 grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-16">
            <div>
              <div className="flex items-end gap-3">
                <span className="font-serif text-5xl leading-none tabular-nums">
                  {stats.average.toFixed(1).replace(".", ",")}
                </span>
                <div className="pb-1">
                  <Stars value={stats.average} size={16} />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Basé sur {stats.count} avis
                  </p>
                </div>
              </div>

              <ul className="mt-6 space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = stats.distribution[star - 1] ?? 0;
                  return (
                    <li
                      key={star}
                      className="flex items-center gap-3 text-xs text-muted-foreground"
                    >
                      <span className="w-8 tabular-nums">{star}★</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
                        <span
                          className="block h-full rounded-full bg-accent"
                          style={{ width: `${(count / stats.count) * 100}%` }}
                        />
                      </span>
                      <span className="w-6 text-right tabular-nums">{count}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <ul className="space-y-6">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </ul>
          </div>
        )}
      </div>

      <ReviewDialog
        open={open}
        onOpenChange={setOpen}
        productSlug={productSlug}
        productName={productName}
      />
    </section>
  );
}
